/* eslint-disable no-console */
import { ApolloClient } from '@apollo/client';
import { get } from 'lodash';
import { Dispatch } from 'redux';
import { change } from 'redux-form';
import { fromJS } from 'immutable';

import {
  ImpactAiGenerationDocument,
  ImpactAiGenerationMutation,
  ImpactAiGenerationMutationVariables,
} from 'generated/graphql';

import {
  CallActionPayload,
  ContextStore,
  ContextValue,
  MethodResponse,
  UIStateCallback,
} from '../../types';
import { ContextAPI } from '../ImpactAiContextProvider';
import { ImpactAiTracker } from '../useImpactAiTracking';

/**
 * Pre-set fields that are not generated by AI
 * @param type - Type discriminator of 'preset'
 * @param fields.key - A string of the path to the field to be set
 * @param fields.value - The value to be set for the field. Can be any type
 * @example
 * const presetBatch: PresetBatch = {
 *   type: 'preset',
 *   fields: {
 *     'nonprofits.name': 'Happy Dancing Cats Inc.',
 *     'nonprofits.amount': 100,
 *   }
 * }
 */
type PresetBatch = {
  type: 'preset';
  fields: {
    [key: string]: ContextValue;
  };
};

/**
 * Fields generated by a call to the ImpactAiGeneration mutation
 * @param type - Type discriminator of 'generative'
 * @param target - The target of the batch, either 'form' or 'context'
 * @param generationId - The id of the managed prompt to use
 * @param contextToPromptMap.source - A string of the path for the value to retrieved from the context store (e.g. 'choices.vibe')
 * @param contextToPromptMap.value - Optional hard-coded value to pass to the prompt (e.g. 'Happy')
 * @note Either source or value must be provided, not both.
 * @param contextToPromptMap.target - A string of the path at which to pass this value to the prompt
 * @param outputToFieldMap.source - A string of the property path on the model output (e.g. 'description')
 * @param outputToFieldMap.target - A string of either the form field name where the model output will be set (e.g. 'description') or the path to the context store value where the model output will be set (e.g. 'options.a.description')
 * @example
 * const generativeFormBatch: GenerativeBatch = {
 *   type: 'generative',
 *   target: 'form',
 *   generationId: 'gen_abc123',
 *   contextToPromptMap: [
 *     { source: 'choices.vibe', target: 'vibe' },
 *     { source: 'choices.cause', target: 'cause' },
 *   ],
 *   outputToFieldMap: [
 *     { source: 'description', target: 'description' },
 *     { source: 'title', target: 'title' },
 *   ]
 * }
 * const generativeContextBatch: GenerativeBatch = {
 *   type: 'generative',
 *   target: 'context',
 *   generationId: 'gen_abc123',
 *   contextToPromptMap: [
 *     { value: 'upbeat', target: 'vibe' }, // Note we use an alternative vibe here for generating an additional option
 *     { source: 'choices.cause', target: 'cause' },
 *   ],
 *   outputToFieldMap: [
 *     { source: 'description', target: 'options.a.description' },
 *     { source: 'title', target: 'options.a.title' },
 *   ]
 * }
 */
type GenerativeBatch = {
  type: 'generative';
  target: 'form' | 'context';
  generationId: string;
  contextToPromptMap: Array<{
    source?: string;
    value?: string;
    target: string;
  }>;
  outputToFieldMap: Array<{
    source: string;
    target: string;
  }>;
};

type Batch = PresetBatch | GenerativeBatch;

export const batchGenerate = async (
  payload: CallActionPayload,
  client: ApolloClient<unknown>,
  contextApi: ContextAPI,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>,
  uiStateCallback?: UIStateCallback,
  tracker?: ImpactAiTracker
): Promise<MethodResponse> => {
  const { batches, form, streamingDelay } = payload as {
    batches: Batch[];
    form: string;
    streamingDelay?: number;
  };

  const { presetId, impactaiSession, presetSession } = tracker?.getSessionContext?.() ?? {};
  const metadata = { presetId, impactaiSession, presetSession };

  if (!batches || !Array.isArray(batches)) {
    throw new Error('batches array is required for batch-generate method');
  }

  // Signal start of processing
  if (uiStateCallback) {
    uiStateCallback('processing');
  }

  // Helper function to calculate adaptive streaming parameters
  const getStreamingParams = (text: string, fieldName: string) => {
    const { length } = text;
    const baseDelay = streamingDelay || 50;

    // Detect content type for smarter streaming
    const isTitle =
      fieldName.toLowerCase().includes('title') || fieldName.toLowerCase().includes('name');
    const isDescription =
      fieldName.toLowerCase().includes('description') ||
      fieldName.toLowerCase().includes('text') ||
      fieldName.toLowerCase().includes('content');
    const isCTA =
      fieldName.toLowerCase().includes('cta') || fieldName.toLowerCase().includes('button');
    const isTagline =
      fieldName.toLowerCase().includes('tagline') || fieldName.toLowerCase().includes('slogan');

    // Base parameters by content length
    let params;
    if (length < 20) {
      // Very short text: satisfying character-by-character
      params = { delay: baseDelay, minChunk: 1, maxChunk: 1 };
    } else if (length < 60) {
      // Short text: mostly character-by-character with occasional 2-char chunks
      params = { delay: Math.max(baseDelay * 0.85, 35), minChunk: 1, maxChunk: 2 };
    } else if (length < 150) {
      // Medium text: small chunks, moderate speed
      params = { delay: Math.max(baseDelay * 0.65, 25), minChunk: 2, maxChunk: 3 };
    } else if (length < 300) {
      // Long text: medium chunks, faster speed
      params = { delay: Math.max(baseDelay * 0.45, 20), minChunk: 3, maxChunk: 5 };
    } else if (length < 600) {
      // Very long text: larger chunks, much faster
      params = { delay: Math.max(baseDelay * 0.3, 15), minChunk: 4, maxChunk: 7 };
    } else {
      // Extremely long text: large chunks, very fast
      params = { delay: Math.max(baseDelay * 0.2, 10), minChunk: 5, maxChunk: 10 };
    }

    // Adjust based on content type for better UX
    if (isTitle || isTagline) {
      // Titles and taglines should feel impactful - slightly slower for drama
      params.delay = Math.min(params.delay * 1.3, baseDelay * 1.1);
      params.maxChunk = Math.max(params.maxChunk - 1, 1); // Smaller chunks for impact
    } else if (isCTA) {
      // CTAs should feel punchy and energetic - faster
      params.delay = Math.max(params.delay * 0.7, 15);
    } else if (isDescription && length > 200) {
      // Long descriptions should stream faster to avoid tedium
      params.delay = Math.max(params.delay * 0.6, 10);
      params.minChunk = Math.min(params.minChunk + 1, 8);
      params.maxChunk = Math.min(params.maxChunk + 3, 12);
    }

    return params;
  };

  // Helper function to set form fields with adaptive streaming support
  const setFormField = async (fieldName: string, value: unknown): Promise<void> => {
    const isUrl = (str: string): boolean => {
      const urlPattern = /^https?:\/\/|^www\./;
      return urlPattern.test(str);
    };

    const streamString = async (valueToStream: string): Promise<void> => {
      if (isUrl(valueToStream)) {
        // Don't stream URLs, set them immediately
        dispatch(change(String(form), fieldName, valueToStream));
        return;
      }

      const { delay, minChunk, maxChunk } = getStreamingParams(valueToStream, fieldName);

      // Clear the field first
      dispatch(change(String(form), fieldName, ''));

      const totalLength = valueToStream.length;
      let charsStreamed = 0;

      const streamChunks = async (currentValue: string, remainingText: string): Promise<void> => {
        if (remainingText.length === 0) {
          return;
        }

        // Calculate progress (0 to 1)
        const progress = charsStreamed / totalLength;

        // Ease-out curve: starts fast, slows down to deliberate pace
        // Using inverse quadratic: 1 - (1 - progress)^2
        const easeFactor = 1 - (1 - progress) ** 2;

        // Calculate dynamic delay (starts at minimum, increases to thoughtful pace)
        const minDelay = Math.max(delay * 0.3, 15); // Start faster but not too fast
        const maxDelay = delay * 1.2; // End even more deliberate than original
        const currentDelay = minDelay + (maxDelay - minDelay) * easeFactor;

        // Calculate dynamic chunk size (starts larger, gets smaller for precision)
        const chunkReduction = Math.floor((maxChunk - minChunk) * easeFactor);
        const currentMaxChunk = maxChunk;
        const currentMinChunk = Math.max(maxChunk - chunkReduction, minChunk);

        // Adaptive chunk size with current parameters
        const chunkSize =
          Math.floor(Math.random() * (currentMaxChunk - currentMinChunk + 1)) + currentMinChunk;
        const chunk = remainingText.slice(0, chunkSize);
        const newRemainingText = remainingText.slice(chunkSize);
        const newCurrentValue = currentValue + chunk;

        charsStreamed += chunk.length;

        dispatch(change(String(form), fieldName, newCurrentValue));

        if (newRemainingText.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, Math.round(currentDelay)));
          await streamChunks(newCurrentValue, newRemainingText);
        }
      };

      await streamChunks('', valueToStream);
    };

    // Handle string streaming
    if (typeof value === 'string' && streamingDelay && streamingDelay > 0) {
      await streamString(value);
      return;
    }

    // Convert complex data types (arrays and objects) to Immutable format
    let processedValue = value;
    if (
      value !== null &&
      (Array.isArray(value) || (typeof value === 'object' && value.constructor === Object))
    ) {
      processedValue = fromJS(value);
    }

    dispatch(change(String(form), fieldName, processedValue));
  };

  // Separate batches by type
  const presetBatches = batches.filter((batch): batch is PresetBatch => batch.type === 'preset');
  const generativeBatches = batches.filter(
    (batch): batch is GenerativeBatch => batch.type === 'generative'
  );

  // Process preset batches immediately
  if (presetBatches.length > 0) {
    await Promise.all(
      presetBatches.map(async (batch) => {
        // Process preset fields sequentially to avoid interference with streaming
        const fieldEntries = Object.entries(batch.fields);
        await fieldEntries.reduce(async (previousPromise, [fieldPath, fieldValue]) => {
          await previousPromise;
          // Set in form if form is provided
          await setFormField(fieldPath, fieldValue);

          // Always update context using contextApi
          contextApi.setValue(fieldPath, fieldValue);
        }, Promise.resolve());
      })
    );
  }

  // Store for failed batches
  const failedBatches: Array<{
    batch: GenerativeBatch;
    error: Error;
  }> = [];

  // Helper function to process a single generative batch
  const processSingleGenerativeBatch = async (batch: GenerativeBatch) => {
    try {
      // Build prompt variables from context
      const promptVariables: Record<string, ContextValue> = {};

      batch.contextToPromptMap.forEach(({ source, value, target }) => {
        if (source && value) {
          throw new Error(
            `Both source and value provided for target "${target}". Only one should be specified.`
          );
        }

        if (source) {
          const contextValue = contextApi.getValue(source);
          if (Array.isArray(contextValue)) {
            promptVariables[target] = JSON.stringify(contextValue);
          } else {
            promptVariables[target] = contextValue;
          }
        } else if (value !== undefined) {
          promptVariables[target] = value;
        } else {
          throw new Error(`Neither source nor value provided for target "${target}"`);
        }
      });

      // Make GraphQL call
      const { data } = await client.mutate<
        ImpactAiGenerationMutation,
        ImpactAiGenerationMutationVariables
      >({
        mutation: ImpactAiGenerationDocument,
        variables: {
          generationId: String(batch.generationId),
          variables: promptVariables,
          metadata,
        },
        fetchPolicy: 'network-only',
      });

      const outputs = data?.impactAiGeneration as ContextStore;

      if (!outputs) {
        throw new Error(`No output received for generationId "${batch.generationId}"`);
      }

      // Map outputs to targets based on batch configuration
      // Process form fields sequentially to avoid interference with streaming
      await batch.outputToFieldMap.reduce(async (previousPromise, { source, target }) => {
        await previousPromise;
        const outputValue = get(outputs, source);

        if (outputValue !== undefined) {
          if (batch.target === 'form') {
            // Set in form with streaming
            await setFormField(target, outputValue);
          }

          // Always update context using contextApi
          contextApi.setValue(target, outputValue);
        }
      }, Promise.resolve());

      return { success: true, generationId: batch.generationId, outputs };
    } catch (error) {
      return { success: false, generationId: batch.generationId, error: error as Error };
    }
  };

  // Process generative batches and collect failures
  if (generativeBatches.length > 0) {
    const generationPromises = generativeBatches.map(processSingleGenerativeBatch);
    const results = await Promise.all(generationPromises);

    // Collect failed batches
    results.forEach((result, index) => {
      if (!result.success && result.error) {
        failedBatches.push({
          batch: generativeBatches[index],
          error: result.error,
        });
      }
    });

    // If there are failures, create retry callback and signal error
    if (failedBatches.length > 0) {
      const handleRetry = async () => {
        try {
          // Retry failed batches
          const retryPromises = failedBatches.map(({ batch }) =>
            processSingleGenerativeBatch(batch)
          );
          const retryResults = await Promise.all(retryPromises);

          // Check if any retries still failed
          const stillFailed = retryResults.some((result) => !result.success);
          if (stillFailed) {
            // Update failed batches with new failures
            const originalFailedBatches = [...failedBatches];
            failedBatches.length = 0; // Clear the array
            retryResults.forEach((result, index) => {
              if (!result.success && result.error) {
                failedBatches.push({
                  batch: originalFailedBatches[index].batch,
                  error: result.error,
                });
              }
            });

            // Signal error again with the same retry callback
            if (uiStateCallback) {
              uiStateCallback('error', handleRetry);
            }
          } else if (uiStateCallback) {
            // All retries succeeded, signal completion
            uiStateCallback('complete');
          }
        } catch (retryError) {
          if (uiStateCallback) {
            uiStateCallback('error', handleRetry);
          }
        }
      };

      if (uiStateCallback) {
        uiStateCallback('error', handleRetry);
      }
      return { error: true };
    }
  }

  // Signal completion if no failures
  if (uiStateCallback) {
    uiStateCallback('complete');
  }

  return {};
};
